import { DocumentOrganizationKeys, GroupSettings } from '@griply/types';
import { useSortedGoals } from 'features/goals';
import {
  getDefaultFilterDocument,
  getDefaultGoalFilterDocument,
  getFilterDocument,
  useUpdateLifeArea,
} from 'features/life-areas';
import { useUpdateTasks } from 'features/tasks';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useMemo } from 'react';
import { rootId } from 'shared/components/ui/virtualized-tree-list';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { UpdateTask } from 'shared/types/task';
import { useGoalsByFilterDocument } from 'shared/views/goal-filter-list';
import {
  noGroupingId,
  ProvidedTaskFilterList,
} from 'shared/views/task-filter-list';

import { useLifeAreaCustomOrderByGrouping } from '../../hooks/use-life-area-custom-order-by-grouping';

type TasksOverviewProps = {
  scrollElement: React.RefObject<HTMLDivElement>;
  lifeArea: LifeArea;
};

export const TasksOverview: React.FC<TasksOverviewProps> = ({
  scrollElement,
  lifeArea,
}) => {
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const { submit: updateTasks } = useUpdateTasks();
  const updateRelationships = useUpdateRelationshipsReferences();
  const allGoals = useSortedGoals();

  const goalFilterDocument = useMemo(
    () => getDefaultGoalFilterDocument(lifeArea, allGoals),
    [allGoals, lifeArea],
  );

  const customGoalOrder = useMemo(
    () => lifeArea.goalSorting ?? undefined,
    [lifeArea.goalSorting],
  );

  const sortedGoals = useGoalsByFilterDocument(goalFilterDocument, {
    customOrder: customGoalOrder,
  });

  const filterDocument = useMemo(() => getFilterDocument(lifeArea), [lifeArea]);

  const lifeAreaGoals = useMemo(
    () =>
      sortedGoals.filter((goal) => {
        const isGoalGrouping =
          filterDocument[DocumentOrganizationKeys.GroupByMode] ===
          GroupByMode.Goal;

        if (isGoalGrouping && goal.archivedAt) {
          return false;
        }

        if (lifeArea.showCompletedTasks) {
          return true;
        }

        const isCompleted = !!goal.completedAt;
        const allTasksCompleted =
          (goal.taskCount ?? 0) === (goal.completedTaskCount ?? 0);

        return !(isCompleted && allTasksCompleted);
      }),
    [sortedGoals, lifeArea.showCompletedTasks, filterDocument],
  );

  const hasActiveFilters = useMemo(
    () =>
      !isEqual(
        filterDocument.filters,
        getDefaultFilterDocument(lifeArea).filters,
      ),
    [filterDocument.filters, lifeArea],
  );

  const groupSettings = useMemo<GroupSettings>(
    () => ({
      [GroupByMode.Section]: {
        sections: lifeArea?.sections,
        onUpdateSection: (sectionId: ID, name: string) => {
          if (!lifeArea) {
            return;
          }
          updateLifeArea({
            id: lifeArea.id,
            sections: lifeArea.sections?.map((s) =>
              s.id === sectionId ? { ...s, name } : s,
            ),
          });
        },
        lifeAreaId: lifeArea?.id,
        showEmptyGroups: !hasActiveFilters,
      },
      [GroupByMode.Goal]: {
        lifeAreaId: lifeArea?.id,
        showEmptyGroups: !hasActiveFilters,
      },
      [GroupByMode.Priority]: { lifeAreaId: lifeArea?.id },
      [GroupByMode.Type]: { lifeAreaId: lifeArea?.id },
      [GroupByMode.Deadline]: { lifeAreaId: lifeArea?.id },
      [GroupByMode.DeadlineDeadline]: { lifeAreaId: lifeArea?.id },
      [GroupByMode.Tag]: { lifeAreaId: lifeArea?.id },
      [GroupByMode.None]: { lifeAreaId: lifeArea?.id },
    }),
    [lifeArea, hasActiveFilters, updateLifeArea],
  );

  const onSectionCollapse = useCallback(
    (collapsedSections: ID[]) => {
      if (!lifeArea) {
        return;
      }

      updateLifeArea({
        id: lifeArea.id,
        filterDocument: {
          ...(lifeArea.filterDocument ?? {}),
          collapsedSections,
        },
      });
    },
    [lifeArea, updateLifeArea],
  );

  const onReorder = useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: UpdateTask[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      if (!lifeArea) {
        return;
      }

      // Handle task updates (e.g. reparenting/move)
      updateTasks(updatedTasks);
      const groupBy =
        filterDocument[DocumentOrganizationKeys.GroupByMode] ??
        GroupByMode.Section;

      if (groupBy === GroupByMode.Section) {
        // Create a map of existing sections for quick lookup
        const sectionMap = new Map(
          (lifeArea.sections ?? []).map((section) => [section.id, section]),
        );

        const newSections: typeof lifeArea.sections = [];
        let rootOrder = lifeArea.sections?.map(({ id }) => id) ?? [];

        // Reconstruct the sections array based on the updated order
        updatedSections.forEach((updatedSection) => {
          if (updatedSection.id === rootId) {
            // when root, we abuse the taskSorting to store the sections order
            rootOrder = updatedSection.taskSorting;
            return;
          }

          const originalSection = sectionMap.get(updatedSection.id);

          if (originalSection) {
            newSections.push({
              ...originalSection,
              tasks: updatedSection.taskSorting,
            });
            sectionMap.delete(updatedSection.id);
          }
        });

        // Add any remaining sections that weren't in the update (safety fallback)
        sectionMap.forEach((remainingSection) =>
          newSections.push(remainingSection),
        );

        // Use the correct sorting
        newSections.sort(
          (a, b) => rootOrder.indexOf(a.id) - rootOrder.indexOf(b.id),
        );

        updateLifeArea({
          id: lifeArea.id,
          sections: newSections,
          taskSorting:
            updatedSections.find((s) => s.id === noGroupingId)?.taskSorting ??
            lifeArea.taskSorting,
          filterDocument: {
            ...(lifeArea.filterDocument ?? {}),
            sortingMode: SortingMode.Custom,
          },
          taskSortingMode: SortingMode.Custom,
        });

        // handle relationship updates if provided
        if (relationshipUpdates?.length) {
          updateRelationships(relationshipUpdates);
        }
        return;
      }

      // handle sorting update for all groupings except 'noGrouping'
      const groupBySorting = updatedSections.reduce<Record<ID, ID[]>>(
        (acc, section) => {
          acc[section.id] = section.taskSorting;
          return acc;
        },
        {},
      );

      if (Object.keys(groupBySorting).length) {
        const _groupBy = groupBy as Exclude<
          GroupByMode,
          GroupByMode.None | GroupByMode.LifeArea
        >;
        updateLifeArea({
          id: lifeArea.id,
          customTaskOrder: {
            ...(lifeArea.customTaskOrder ?? {}),
            [_groupBy]: {
              ...(lifeArea.customTaskOrder?.[_groupBy] ?? {}),
              ...groupBySorting,
            },
          },
          taskSortingMode: SortingMode.Custom,
          filterDocument: {
            ...(lifeArea.filterDocument ?? {}),
            sortingMode: SortingMode.Custom,
          },
        });
      }

      // handle relationship updates if provided
      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [
      filterDocument,
      lifeArea,
      updateLifeArea,
      updateRelationships,
      updateTasks,
    ],
  );

  const customOrder = useLifeAreaCustomOrderByGrouping(
    filterDocument.groupByMode,
    lifeArea,
  );

  if (!lifeArea) {
    return null;
  }

  return (
    <ProvidedTaskFilterList
      filterDocument={filterDocument}
      customOrder={customOrder}
      onReorder={onReorder}
      onSectionCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      groupSettings={groupSettings}
      goals={lifeAreaGoals}
    />
  );
};
